import React from "react";
import { PUBLISH_CHECKS } from "./text.js";

/** Generating: spinner, the job line, an indeterminate bar, and how long it usually takes. */
export function GeneratingPanel({ code, title, detail }) {
  return (
    <div role="status" aria-live="polite" className="wr-status" data-testid={`report-generating-${code}`}>
      <div className="wr-status-row">
        <span className="wr-spin" aria-hidden="true" />
        <strong style={{ fontWeight: 600 }}>{title}</strong>
      </div>
      <div className="wr-bar" aria-hidden="true"><span /></div>
      <span className="wr-statusnote">{detail}</span>
    </div>
  );
}

/** Approve flow, panel 2 of Approve.dc.html: approved, and the PDF is being made. */
export function ApprovingPanel({ code, title, detail, making = "Making the PDF…" }) {
  return (
    <div role="status" aria-live="polite" className="wr-status card" data-testid={`report-approving-${code}`}>
      <div className="wr-status-row">
        <span className="wr-okmark" aria-hidden="true">✓</span>
        <strong style={{ fontSize: 15, fontWeight: 600 }}>{title}</strong>
      </div>
      <div className="wr-status-row">
        <span className="wr-spin" aria-hidden="true" />
        <span>{making}</span>
      </div>
      <div className="wr-bar" aria-hidden="true"><span /></div>
      <span className="wr-statusnote">{detail}</span>
    </div>
  );
}

/** Retry PDF: only the PDF step is running (the report is already approved). */
export function PdfRetryPanel({ code }) {
  return (
    <div role="status" aria-live="polite" className="wr-status card" data-testid={`report-pdf-retrying-${code}`}>
      <div className="wr-status-row">
        <span className="wr-spin" aria-hidden="true" />
        <span>Making the PDF…</span>
      </div>
      <div className="wr-bar" aria-hidden="true"><span /></div>
      <span className="wr-statusnote">Usually under 30 seconds. The buttons stay off until it is done.</span>
    </div>
  );
}

/**
 * Publish flow, panel 5: the checklist is driven by the ONE publish call --
 * pending while it runs, then done, or failed with the refusal. The screen
 * cannot see the server's inner steps, so it never claims one finished before
 * the call has.
 */
export function PublishingPanel({ code, title, state = "pending", message = "", doneCount = 0 }) {
  const checks = PUBLISH_CHECKS(code);
  const mark = state === "done" ? "✓" : state === "failed" ? "✕" : "◌";
  const cls = state === "done" ? "done" : state === "failed" ? "fail" : "wait";
  return (
    <div role="status" aria-live="polite" className="wr-status card tight" data-testid={`report-publishing-${code}`} data-state={state}>
      <div className="wr-status-row">
        {state === "pending" ? <span className="wr-spin" aria-hidden="true" /> : null}
        <strong style={{ fontWeight: 600 }}>{title}</strong>
      </div>
      <div className="wr-checks">
        {checks.map((c, i) => (
          <span key={c} className={state !== "pending" ? cls : i < doneCount ? "done" : i === doneCount ? "now" : "wait"}>{state === "pending" && i < doneCount ? "✓" : mark} {c}</span>
        ))}
      </div>
      <span className="wr-statusnote">{message || "Takes a few seconds. Nothing is published unless every check passes."}</span>
    </div>
  );
}

/** Approve flow, panel 3: the PDF is ready, with its file name, and not yet visible to the client. */
export function PdfReadyCard({ code, version, fileName, sizeLabel, children }) {
  return (
    <div className="wr-pdfcard" data-testid={`report-pdf-ready-${code}`} role="status" aria-live="polite">
      <strong style={{ fontSize: 15, fontWeight: 600 }}>Approved{version ? ` v${version}` : ""} · PDF ready</strong>
      <div className="wr-file">
        <span className="wr-pdftag">PDF</span>
        <span>
          <span className="name">{fileName || "Approved report.pdf"}</span>
          <span style={{ color: "var(--wr-t3)", fontSize: 12 }}>{sizeLabel ? `${sizeLabel} · ` : ""}not yet visible to the client</span>
        </span>
      </div>
      {children}
    </div>
  );
}

/** Approve flow, panel 6: published. */
export function PublishedCard({ code, label, stamp, siteUrl, onNext, nextLabel, linkBox }) {
  return (
    <div className="wr-pdfcard ok" data-testid={`report-published-${code}`} role="status" aria-live="polite">
      <div className="wr-status-row">
        <span className="wr-okmark" aria-hidden="true">✓</span>
        <strong style={{ fontSize: 15, fontWeight: 600 }}>{label} published{stamp ? ` · ${stamp}` : ""}</strong>
      </div>
      <span style={{ color: "var(--wr-t5)" }}>The client can now open it on the {code} project site.</span>
      <div className="wr-actions">
        {siteUrl ? (
          <a className="wr-btn" style={{ borderColor: "var(--wr-ok-br)", boxSizing: linkBox }} href={siteUrl} target="_blank" rel="noopener noreferrer">Open on project site</a>
        ) : null}
        {onNext ? <button type="button" className="wr-btn" onClick={onNext}>{nextLabel}</button> : null}
      </div>
    </div>
  );
}

/** Something in the approve / PDF / publish flow was refused: say what, and what is still true. */
export function ErrorBox({ code, title, message, url, onDismiss }) {
  return (
    <div className="wr-errbox" role="alert" data-testid={`report-flow-error-${code}`}>
      <strong>{title}</strong>
      <span>{message}</span>
      {url ? (
        <span>
          <a href={url} target="_blank" rel="noopener noreferrer">Open the file already on the project site</a>
        </span>
      ) : null}
      {onDismiss ? (
        <button type="button" className="wr-linkbtn" onClick={onDismiss}>Dismiss</button>
      ) : null}
    </div>
  );
}
